import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { billsQueryOptions } from '@/features/bills/api'
import { BillsScreen } from '@/features/bills/bills-screen'
import { type BillsSearch, billsSearchSchema, toBillListParams } from '@/features/bills/search'

/**
 * Layout PATHLESS de Pagamentos › Pagar.
 *
 * `_pagar` não entra na URL: ele renderiza a tela + <Outlet/>, e os filhos
 * (`pagar.tsx` = null, `agendar.tsx` = drawer) montam no Outlet.
 *
 * Por que assim: no nibo, "Agendar" é tab IRMÃ de "Pagar", mas o drawer abre POR
 * CIMA da lista. Se `agendar` fosse rota irmã, a lista DESMONTARIA — perderia
 * tabela, scroll, seleção, e refaria o fetch ao fechar. Aqui a lista permanece
 * montada, o back fecha o drawer, e o validateSearch mora no pai, então os
 * filtros sobrevivem ao ciclo do drawer de graça.
 *
 * A lista lê `/v1/bills` — as DUAS origens — e não `/v1/schedules`: conta vinda
 * do banco é conta a pagar do mesmo jeito.
 */
export const Route = createFileRoute('/_authed/pagamentos/_pagar')({
  validateSearch: billsSearchSchema,
  loaderDeps: ({ search }) => ({ params: toBillListParams(search, 'payable') }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(billsQueryOptions(deps.params)),
  component: PagarLayout,
})

function PagarLayout() {
  const { t } = useTranslation(['nav'])
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  return (
    <>
      <BillsScreen
        direction="payable"
        title={t('nav:payables.pay')}
        // Botão explícito na listagem: a tab "Agendar" no topo passa despercebida.
        novoTo="/pagamentos/agendar"
        novoLabel={t('nav:payables.newSchedule')}
        search={search}
        onSearchChange={(patch) => {
          // `to` EXPLÍCITO para a folha: sem ele, `navigate` a partir deste
          // layout PATHLESS resolve o destino como o próprio layout (não a rota
          // `pagar` que casa na URL), e a navegação vira no-op — filtro,
          // paginação e "linhas" não disparavam request nenhum.
          //
          // `prev` ANOTADO: o `to` conserta o runtime, mas não basta para o tipo
          // aqui — sem a anotação o updater cai em `any` (TS7006).
          void navigate({
            to: '/pagamentos/pagar',
            search: (prev: BillsSearch) => ({ ...prev, ...patch }),
            replace: true,
          })
        }}
      />
      <Outlet />
    </>
  )
}
